import React, { useMemo } from "react";
import LabeledField from "../../../Views/Home/InquiryRotationView/InquirySchemeManagement/components/LabeledField";
import { Inputs, SelectComponet, SwitchComponent } from "../../Controls";
import { leadTypes } from "../LeadsEnum";
import { LeadTypeIdEnum } from "../../../Enums";
import LookupAutocomplete from "../../Controls/LookupAutocomplete/LookupAutocomplete";
import { StaticLookupsIds } from "../../../assets/json/StaticLookupsIds";
import PropertyAutocomplete from "./PropertyAutocomplete";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogActions,
  makeStyles,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";
import UnitsAutocomplete from "./UnitsAutocomplete";
import { useSelector } from "react-redux";
import { mapOwnerLeadDataToPostDto, mapUserDataToReferredBy } from "../models";
import { leadPost, propertyDetailsGet } from "../../../Services";
import { useCreateLeadContext } from "../context/CreateLeadContext";
import { getErrorByName, showError, showSuccess } from "../../../Helper";
import { Spinner } from "../../SpinnerComponent/Spinner";
import { ownerLeadFormSchema } from "../validationSchemas/ownerLeadFormSchema";
import { useSuccessDialog } from "../../../SharedComponents";
import {
  CustomButton,
  CustomSelect,
  CustomSwitch,
  ReskinningInputs,
} from "../../V2";
import { useLayoutDetection } from "../../../Hooks";

const styles = makeStyles({
  addButton: {
    backgroundColor: "#7F6944",
    borderColor: "#7F6944",
    textTransform: "capitalize",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#6b5c3a",
    },
  },
  cancelButton: {
    backgroundColor: "#FFFFFF",
    borderColor: "#D0D5DD",
    textTransform: "capitalize",
    color: "#344054",
  },
  customSelect: {
    "& .MuiInput-root": {
      width: "100% !important",
      maxWidth: "512px",
    },
  },
});
const OwnerLeadForm = ({ onClose, contact }) => {
  const { loginResponse } = useSelector((state) => state.login);
  const { initialStage, initialStatus, methodOfContact, leadOwnerType } =
    useCreateLeadContext();
  const { showDialog } = useSuccessDialog();
  const [loading, setLoading] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const classes = styles();
  const referredBy = useMemo(
    () => mapUserDataToReferredBy(loginResponse),
    [loginResponse]
  );
  const [lead, setLead] = React.useState({
    leadOperation: "",
    community: null,
    subCommunity: null,
    saleType: null,
    property: null,
    extraNotes: "",
    isMortgage: false,
    country: null,
    city: null,
    district: null,
    unit: null,
  });
  const handleLeadChange = async (field, value) => {
    switch (field) {
      case "country":
        setLead((prevLead) => ({
          ...prevLead,
          country: value,
          city: null,
          district: null,
          community: null,
          subCommunity: null,
        }));
        break;
      case "city":
        setLead((prevLead) => ({
          ...prevLead,
          city: value,
          district: null,
          community: null,
          subCommunity: null,
        }));
        break;
      case "district":
        setLead((prevLead) => ({
          ...prevLead,
          district: value,
          community: null,
          subCommunity: null,
        }));
        break;
      case "community":
        setLead((prevLead) => ({
          ...prevLead,
          community: value,
          subCommunity: null,
        }));
        break;
      case "property":
        setLead((prevLead) => ({
          ...prevLead,
          property: value,
        }));
        if (value?.propertyId) {
          const propertyDetails = await propertyDetailsGet({
            id: value.propertyId,
          });
          if (
            !propertyDetails ||
            (propertyDetails.status && propertyDetails.status !== 200)
          )
            return;
          setLead((prevLead) => ({
            ...prevLead,
            country: propertyDetails.property.country || null,
            city: propertyDetails.property.city || null,
            district: propertyDetails.property.district || null,
            community: propertyDetails.property.community || null,
            subCommunity: propertyDetails.property.sub_community || null,
            property: value,
          }));
        }
        break;
      default:
        setLead((prevLead) => ({
          ...prevLead,
          [field]: value,
        }));
        break;
    }
  };

  const { isNewLayout: isReskinningLayout, hasNewLayout } =
    useLayoutDetection();
  const isNewLayout = isReskinningLayout && hasNewLayout;
  const schema = ownerLeadFormSchema.validate(lead, {
    abortEarly: false,
    allowUnknown: true,
  });
  const createLeadHandler = async () => {
    setIsSubmitted(true);
    setLoading(true);
    try {
      if (schema.error) {
        throw new Error("Please fill all required fields");
      }
      const body = mapOwnerLeadDataToPostDto({
        ...lead,
        referredBy,
        status: initialStatus,
        leadStage: initialStage,
        contact,
        methodOfContact,
        leadOwnerType,
      });
      const res = await leadPost(body);
      if ("leadId" in res) {
        showSuccess("Lead created successfully");
        showDialog("Lead created successfully", {
          name:
            contact?.contact?.company_name ??
            `${contact?.contact?.first_name} ${contact?.contact?.last_name}`,
          id: res.leadId,
        });
        onClose();
      } else {
        throw new Error("something went wrong, please try again later");
      }
    } catch (error) {
      showError(
        error.message ?? "something went wrong, please try again later"
      );
    } finally {
      setLoading(false);
    }
  };
  console.log(isNewLayout);
  return (
    <div className="inquiry-management-container p-3">
      <Spinner isActive={loading} isAbsolute />
      <LabeledField
        label={"Operation Type*"}
        component={
          !isNewLayout ? (
            <SelectComponet
              data={leadTypes.filter(
                (item) =>
                  item.leadTypeCategoryId === LeadTypeIdEnum.Owner.leadTypeId
              )}
              valueInput={"id"}
              textInput={"name"}
              value={
                leadTypes.find((item) => item.name === lead.leadOperation)?.id
              }
              multiple={false}
              onSelectChanged={(value) =>
                handleLeadChange(
                  "leadOperation",
                  leadTypes.find((item) => item.id === value).name
                )
              }
              error={getErrorByName(schema, "leadOperation").error}
              helperText={getErrorByName(schema, "leadOperation").message}
              isWithError
              isSubmitted={isSubmitted}
            />
          ) : (
            <Box className={classes.customSelect}>
              <CustomSelect
                options={leadTypes.filter(
                  (item) =>
                    item.leadTypeCategoryId === LeadTypeIdEnum.Owner.leadTypeId
                )}
                onValueChange={(value) =>
                  handleLeadChange(
                    "leadOperation",
                    leadTypes.find((item) => item.id === value).name
                  )
                }
                disabled={false}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                error={
                  getErrorByName(schema, "leadOperation").error && isSubmitted
                }
                helperText={getErrorByName(schema, "leadOperation").message}
              />
            </Box>
          )
        }
        hideDivider
      />
      <LabeledField
        label={"Community*"}
        component={
          <LookupAutocomplete
            lookupTypeId={StaticLookupsIds.Community}
            onChange={(_e, value) => handleLeadChange("community", value)}
            lookupParentId={lead.district?.lookupItemId}
            selectedValues={lead.community}
            error={getErrorByName(schema, "community").error}
            helperText={getErrorByName(schema, "community").message}
            withError
            isSubmitted={isSubmitted}
            disabled={lead.property}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Sub Community"}
        component={
          <LookupAutocomplete
            lookupTypeId={StaticLookupsIds.SubCommunity}
            onChange={(_e, value) => handleLeadChange("subCommunity", value)}
            selectedValues={lead.subCommunity}
            lookupParentId={lead.community?.lookupItemId}
            disabled={!lead.community || lead.property}
            error={getErrorByName(schema, "subCommunity").error}
            helperText={getErrorByName(schema, "subCommunity").message}
            withError
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Sale Type"}
        component={
          <LookupAutocomplete
            lookupName="UnitSaleType"
            onChange={(_e, value) => handleLeadChange("saleType", value)}
            selectedValues={lead.saleType}
            error={getErrorByName(schema, "saleType").error}
            helperText={getErrorByName(schema, "saleType").message}
            withError
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Property name"}
        component={
          <PropertyAutocomplete
            value={lead.property}
            onChange={(_e, value) => handleLeadChange("property", value)}
            error={getErrorByName(schema, "property").error}
            helperText={getErrorByName(schema, "property").message}
            isWithError
            isSubmitted={isSubmitted}
            isNewLayout={isNewLayout}
          />
        }
      />
      <LabeledField
        label={"Extra notes"}
        component={
          isNewLayout ? (
            <ReskinningInputs
              multiline
              rows={4}
              inputPlaceholder={"Add notes or requirements"}
              value={lead.extraNotes}
              onInputChanged={(e) =>
                handleLeadChange("extraNotes", e.target.value)
              }
              isWithError
              isSubmitted={isSubmitted}
              error={getErrorByName(schema, "extraNotes").error}
              helperText={getErrorByName(schema, "extraNotes").message}
            />
          ) : (
            <Inputs
              multiline
              rows={4}
              inputPlaceholder={"Add notes or requirements"}
              value={lead.extraNotes}
              onInputChanged={(e) =>
                handleLeadChange("extraNotes", e.target.value)
              }
              isWithError
              isSubmitted={isSubmitted}
              error={getErrorByName(schema, "extraNotes").error}
              helperText={getErrorByName(schema, "extraNotes").message}
            />
          )
        }
      />
      <Accordion className="my-2">
        <AccordionSummary expandIcon={<ExpandMore />}>
          View more optional details
        </AccordionSummary>
        <AccordionDetails>
          <div className="d-flex-column">
            {isNewLayout ? (
              <CustomSwitch
                label={"Is Mortgage"}
                labelPlacement={"end"}
                onChangeHandler={(_e, value) =>
                  handleLeadChange("isMortgage", value)
                }
              />
            ) : (
              <SwitchComponent
                label={"Is Mortgage"}
                labelPlacement={"end"}
                onChangeHandler={(_e, value) =>
                  handleLeadChange("isMortgage", value)
                }
              />
            )}
            <LabeledField
              label={"County"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.Country}
                  onChange={(_e, value) => handleLeadChange("country", value)}
                  selectedValues={lead.country}
                  disabled={lead.property}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"City"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.Cities}
                  onChange={(_e, value) => handleLeadChange("city", value)}
                  lookupParentId={lead.country?.lookupItemId}
                  selectedValues={lead.city}
                  disabled={lead.property}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"District"}
              component={
                <LookupAutocomplete
                  lookupTypeId={StaticLookupsIds.District}
                  onChange={(_e, value) => handleLeadChange("district", value)}
                  lookupParentId={lead.city?.lookupItemId}
                  selectedValues={lead.district}
                  disabled={lead.property}
                  isNewLayout={isNewLayout}
                />
              }
            />
            <LabeledField
              label={"Unit"}
              component={
                <UnitsAutocomplete
                  value={lead.unit}
                  onChange={(_e, value) => handleLeadChange("unit", value)}
                  propertyId={lead.property?.propertyId}
                  isSaleUnitType={
                    lead.leadOperation?.toLowerCase() === "seller"
                  }
                  isNewLayout={isNewLayout}
                />
              }
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <hr />
      <DialogActions>
        {isNewLayout ? (
          <>
            <CustomButton
              variant="outlined"
              onClick={onClose}
              color={"secondary"}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              onClick={createLeadHandler}
              disabled={loading}
            >
              Add owner lead
            </CustomButton>
          </>
        ) : (
          <>
            <Button
              variant="outlined"
              onClick={onClose}
              className={classes.cancelButton}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={createLeadHandler}
              disabled={loading}
            >
              Add owner lead
            </Button>
          </>
        )}
      </DialogActions>
    </div>
  );
};
export default OwnerLeadForm;
